import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorMessage, Field, Form, Formik } from "formik";
import DatePicker from "react-date-picker";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import Select from "react-select";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";
import {
    buildDivisionRegistrationPayload,
    customMultiSelectStyles,
    fromApiTime,
    parseApiDate,
    parseEventUserDiv,
    validateDivisionRegistrationForm,
} from "./divisionRegistrationEventUtils";

const UpdateDivisionRegistrationEvent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { accessToken, userData } = useSelector((state) => state.user);
    const [orgList, setOrgList] = useState([]);
    const [divList, setDivList] = useState([]);
    const [initialValues, setInitialValues] = useState(null);
    const eventId = location.state?.event_id;

    const filteredDivList = useMemo(() => {
        return (orgId) => divList.filter((div) => parseInt(div.org_id) === parseInt(orgId) || parseInt(orgId) === 0);
    }, [divList]);

    const FormikTimePicker = ({ field, form, ...props }) => (
        <TimePicker
            {...props}
            onChange={(val) => form.setFieldValue(field.name, val || "")}
            value={field.value || null}
        />
    );

    useEffect(() => {
        if (!eventId || eventId < 1) {
            navigate("/events");
            return;
        }
        if (!["super_admin", "org_admin"].includes(userData.role_slug)) {
            navigate("/events");
            return;
        }

        dispatch(setPageTitle("Edit Division Registration Event"));
        dispatch(setMainLoading(true));

        const promises = [
            PostAxios(`event/view-event-by-id`, { event_id: eventId }, accessToken),
        ];
        if (userData.role_slug === "super_admin") {
            promises.push(PostAxios(`masters/organization`, { page: 1, perPage: 1000 }, accessToken));
        }
        promises.push(
            PostAxios(
                `masters/division-list`,
                { org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id, page: 1, perPage: 1000 },
                accessToken
            )
        );

        Promise.all(promises)
            .then((responses) => {
                const eventRes = responses[0];
                if (eventRes.data.status !== "1" || eventRes.data.data?.event_mode !== "division_registration") {
                    dispatch(setToastData({ message: "Invalid division registration event.", type: "error", showCloseBtn: true }));
                    navigate("/events");
                    return;
                }

                responses.slice(1).forEach((res) => {
                    if (res.config.url === "masters/organization" && res.data.status === "1") {
                        setOrgList(res.data.data);
                    }
                    if (res.config.url === "masters/division-list" && res.data.status === "1") {
                        setDivList(res.data.data);
                    }
                });

                const event = eventRes.data.data;
                setInitialValues({
                    event_organization: parseInt(event.event_organization) || 0,
                    event_user_div: parseEventUserDiv(event.event_user_div),
                    event_title: event.event_title || "",
                    registration_start_date: parseApiDate(event.registration_start_date),
                    registration_end_date: parseApiDate(event.registration_end_date),
                    event_start_date: parseApiDate(event.event_start_date),
                    event_end_date: parseApiDate(event.event_end_date),
                    event_start_time: fromApiTime(event.event_start_time),
                    attendence_time: fromApiTime(event.attendence_time),
                    event_description: event.event_description || "",
                    event_location: event.event_location || "",
                });
            })
            .catch((err) => {
                responseCatchFunc(dispatch, err);
                navigate("/events");
            })
            .finally(() => dispatch(setMainLoading(false)));
    }, [accessToken, dispatch, eventId, navigate, userData.role_slug]);

    if (!["super_admin", "org_admin"].includes(userData.role_slug)) {
        return <div className="p-3">Only Super Admin or Org Admin can edit division registration events.</div>;
    }

    if (!initialValues) return null;

    return (
        <div className="main-form-sec">
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validate={validateDivisionRegistrationForm}
                onSubmit={(values) => {
                    if (!navigator.onLine) {
                        dispatch(setToastData({ message: "Internet connection is required to update an event.", type: "error", showCloseBtn: true }));
                        return;
                    }
                    dispatch(setMainLoading(true));
                    PostAxios(`event/update-division-registration-event`, buildDivisionRegistrationPayload(values, eventId), accessToken)
                        .then(({ data }) => {
                            if (data.status === "1") {
                                dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                                navigate("/events");
                            } else {
                                dispatch(setToastData({
                                    message: Object.values(data.errors || {}).length ? ConvertErrorArrayToString(data.errors) : data.message,
                                    type: "error",
                                    showCloseBtn: true,
                                }));
                            }
                        })
                        .catch((err) => responseCatchFunc(dispatch, err))
                        .finally(() => dispatch(setMainLoading(false)));
                }}
            >
                {({ values, setFieldValue, errors, touched }) => {
                    const divisionOptions = [
                        { label: "All Divisions", value: "0" },
                        ...filteredDivList(values.event_organization).map((div) => ({
                            label: `${div.division_name} (${div.division_number})`,
                            value: String(div.ID),
                        })),
                    ];
                    return (
                        <Form>
                            <div className="form-sec">
                                <div className="form-field-group">
                                    <h4 className="mb-3 w-100">Edit Division Registration Event</h4>
                                    <p className="text-muted">Update dates, times, divisions, and event details. Registered members and QR code are preserved.</p>
                                </div>

                                {userData.role_slug === "super_admin" && (
                                    <div className={`form-field-group w-50 ${(errors.event_organization && touched.event_organization) ? "error-input" : ""}`}>
                                        <div className="w-50">
                                            <label>Organization *</label>
                                        </div>
                                        <div className="w-50">
                                            <label className="select-input-outer select-100">
                                                <Field
                                                    as="select"
                                                    name="event_organization"
                                                    className="select-input"
                                                    onChange={(e) => {
                                                        setFieldValue("event_organization", parseInt(e.target.value));
                                                        setFieldValue("event_user_div", ["0"]);
                                                    }}
                                                >
                                                    <option value={0}>Select Organization</option>
                                                    {orgList.map((org) => (
                                                        <option key={org.ID} value={org.ID}>{org.org_name}</option>
                                                    ))}
                                                </Field>
                                            </label>
                                            <ErrorMessage name="event_organization" component="span" className="error-label" />
                                        </div>
                                    </div>
                                )}

                                <div className={`form-field-group w-50 ${(errors.event_user_div && touched.event_user_div) ? "error-input" : ""}`}>
                                    <div className="w-50">
                                        <label>Divisions *</label>
                                    </div>
                                    <div className="w-50">
                                        <Select
                                            styles={customMultiSelectStyles}
                                            options={divisionOptions}
                                            placeholder="Select divisions"
                                            value={divisionOptions.filter((opt) => values.event_user_div.includes(opt.value))}
                                            onChange={(selectedValues) => {
                                                const selected = selectedValues || [];
                                                if (!selected.length) {
                                                    setFieldValue("event_user_div", []);
                                                    return;
                                                }
                                                if (selected.some((option) => option.value === "0")) {
                                                    setFieldValue("event_user_div", ["0"]);
                                                } else {
                                                    setFieldValue("event_user_div", selected.map((option) => option.value));
                                                }
                                            }}
                                            isSearchable
                                            isMulti
                                            isClearable
                                            menuPortalTarget={document.body}
                                        />
                                        <ErrorMessage name="event_user_div" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_title && touched.event_title) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Event Title *</label></div>
                                    <div className="w-50 input-area">
                                        <Field name="event_title" className="form-input" placeholder="Event Title" />
                                        <ErrorMessage name="event_title" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_description && touched.event_description) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Description *</label></div>
                                    <div className="w-50 input-area">
                                        <Field as="textarea" name="event_description" className="form-input form-textarea" rows={5} placeholder="Description" />
                                        <ErrorMessage name="event_description" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.registration_start_date && touched.registration_start_date) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Registration Start Date *</label></div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("registration_start_date", val)}
                                                clearIcon={null}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.registration_start_date}
                                                className="form-input"
                                                format="dd-MM-y"
                                            />
                                        </div>
                                        <ErrorMessage name="registration_start_date" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.registration_end_date && touched.registration_end_date) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Registration End Date *</label></div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("registration_end_date", val)}
                                                clearIcon={null}
                                                minDate={values.registration_start_date || undefined}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.registration_end_date}
                                                className="form-input"
                                                format="dd-MM-y"
                                            />
                                        </div>
                                        <ErrorMessage name="registration_end_date" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_start_date && touched.event_start_date) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Attendance Start Date *</label></div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("event_start_date", val)}
                                                clearIcon={null}
                                                minDate={values.registration_end_date || undefined}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.event_start_date}
                                                className="form-input"
                                                format="dd-MM-y"
                                            />
                                        </div>
                                        <ErrorMessage name="event_start_date" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_end_date && touched.event_end_date) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Attendance End Date *</label></div>
                                    <div className="w-50">
                                        <div className="date-picker-sec">
                                            <DatePicker
                                                onChange={(val) => setFieldValue("event_end_date", val)}
                                                clearIcon={null}
                                                minDate={values.event_start_date || undefined}
                                                dayPlaceholder="DD"
                                                monthPlaceholder="MM"
                                                yearPlaceholder="YYYY"
                                                value={values.event_end_date}
                                                className="form-input"
                                                format="dd-MM-y"
                                            />
                                        </div>
                                        <ErrorMessage name="event_end_date" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_start_time && touched.event_start_time) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Daily Attendance Start Time *</label></div>
                                    <div className="w-50">
                                        <Field
                                            name="event_start_time"
                                            component={FormikTimePicker}
                                            className="form-input"
                                            placeholder="Start Time"
                                            clockIcon={null}
                                            disableClock={true}
                                            clearIcon={null}
                                            hourPlaceholder="HH"
                                            minutePlaceholder="mm"
                                        />
                                        <ErrorMessage name="event_start_time" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.attendence_time && touched.attendence_time) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Daily Attendance End Time *</label></div>
                                    <div className="w-50">
                                        <Field
                                            name="attendence_time"
                                            component={FormikTimePicker}
                                            className="form-input"
                                            placeholder="End Time"
                                            clockIcon={null}
                                            disableClock={true}
                                            clearIcon={null}
                                            hourPlaceholder="HH"
                                            minutePlaceholder="mm"
                                        />
                                        <ErrorMessage name="attendence_time" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className={`form-field-group w-50 ${(errors.event_location && touched.event_location) ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Location *</label></div>
                                    <div className="w-50 input-area">
                                        <Field name="event_location" className="form-input" placeholder="Location" />
                                        <ErrorMessage name="event_location" component="span" className="error-label" />
                                    </div>
                                </div>

                                <div className="btm-right-btn justify-content-center my-3">
                                    <button type="submit" className="btn btn-blue">Update Event</button>
                                    <button type="button" className="btn-blue btn ms-2" onClick={() => navigate("/events")}>Cancel</button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default UpdateDivisionRegistrationEvent;
